// src/lib/tutor-tools/sage-action-builders.ts
//
// The only places a SageAction (packages/core/src/sage-action.ts) is made.
// Each builder takes facts the server has already checked (question ids it
// picked, a lesson row it found, the student's first step), builds the href
// through the same helper the rest of the app uses for that page, and runs the
// result through parseSageAction — the check every renderer runs too. So a
// builder returns an action a card WILL render, or null; never a link that
// only looks right.
//
// Pure (no db): offer_activity calls these after its reads, the per-question
// chat route calls buildPracticeAction for a question's topic, and client code
// may call sageActionFromToolResult to draw a card from a stored tool result.

import {
  DRILL_COUNT,
  PRACTICE_COUNT,
  buildPracticeHref,
  parseSageAction,
  questionsSeconds,
  type SageAction,
  type SageActionDifficulty,
} from '@unlimitedtests/core'
import { formatMinutes } from '@/lib/school/assignment-length'
import { drillLabel, sageDrillHref } from '@/lib/practice/sage-drill'
import { CHECKPOINT_COUNT, checkpointHref, isPathTopic } from '@/lib/learn/learning-path'
import { resolveTopicName } from '@/lib/learn/topic-aliases'
import type { FirstStep } from '@/lib/onboarding/first-step'

const DIFFICULTIES: readonly SageActionDifficulty[] = ['E', 'M', 'H', 'mixed']

/**
 * How long a drill of `count` questions takes, in whole minutes: the same pace
 * check_plan_time uses (packages/core session-pace.ts), extended time included.
 * One number for the card, the model's note and a plan check, so Sage's "about
 * N minutes" can never disagree with the card it sits over.
 */
export function drillMinutes(
  count: number,
  section: 'RW' | 'MATH' | null | undefined,
  multiplier = 1,
): number {
  return Math.max(1, Math.ceil(questionsSeconds(count, section ?? 'ALL', multiplier) / 60))
}

/** The card's time line: "About 2 minutes". */
export function drillTimeNote(minutes: number): string {
  return `About ${formatMinutes(minutes)}`
}

/** A Sage drill: the questions picked, in order. Null for none, or more than a drill holds. */
export function drillAction(input: {
  ids: readonly string[]
  topic: string
  difficulty: SageActionDifficulty
  /** The card's time line (drillTimeNote). Omitted for a rebuilt legacy card. */
  note?: string | null
}): SageAction | null {
  const count = input.ids.length
  if (count === 0 || count > DRILL_COUNT.max) return null
  const label = drillLabel(count, input.topic, input.difficulty)
  return parseSageAction({
    v: 1,
    kind: 'drill',
    topic: input.topic,
    count,
    difficulty: input.difficulty,
    href: sageDrillHref({
      ids: input.ids,
      label,
      topic: input.topic,
      difficulty: input.difficulty,
    }),
    label,
    cta: 'Start',
    note: input.note ?? null,
  })
}

/** A learning-path step's skill check — always CHECKPOINT_COUNT questions. Null off the path. */
export function skillCheckAction(topic: string): SageAction | null {
  if (!isPathTopic(topic)) return null
  return parseSageAction({
    v: 1,
    kind: 'skill_check',
    topic,
    count: CHECKPOINT_COUNT,
    difficulty: null,
    href: checkpointHref(topic),
    label: `${topic} skill check`,
    cta: 'Start skill check',
    note: null,
  })
}

/** A lesson the caller found in the Lesson table (the page 404s for any other slug). */
export function lessonAction(lesson: { slug: string; topic: string }): SageAction | null {
  return parseSageAction({
    v: 1,
    kind: 'lesson',
    topic: lesson.topic,
    count: null,
    difficulty: null,
    href: `/learn/${lesson.slug}`,
    label: `${lesson.topic} lesson`,
    cta: 'Read lesson',
    note: null,
  })
}

/**
 * The student's first step (src/lib/onboarding/first-step.ts), with the same
 * title, button text and duration every other surface shows for it.
 */
export function firstStepAction(step: FirstStep): SageAction | null {
  return parseSageAction({
    v: 1,
    kind: 'first_step',
    topic: null,
    count: null,
    difficulty: null,
    href: step.href,
    label: step.title,
    cta: step.cta,
    note: step.duration,
  })
}

/**
 * Topic practice for the per-question Sage surfaces (inline chat in practice
 * and review, and the phone), which run no tools: the route builds this from
 * the question's own topic. Null when the topic doesn't resolve to one we
 * know, so no button is shown rather than one that opens an empty session.
 */
export function buildPracticeAction(
  rawTopic: string | null | undefined,
  count: number = PRACTICE_COUNT.default,
): SageAction | null {
  if (typeof rawTopic !== 'string') return null
  const resolved = resolveTopicName(rawTopic)
  if (!resolved) return null
  const n = Math.min(PRACTICE_COUNT.max, Math.max(PRACTICE_COUNT.min, Math.round(count)))
  return parseSageAction({
    v: 1,
    kind: 'practice',
    topic: resolved.name,
    count: n,
    difficulty: null,
    href: buildPracticeHref(resolved.name, n),
    label: drillLabel(n, resolved.name, 'mixed'),
    cta: 'Practice',
    note: null,
  })
}

/**
 * A card for a generate_drill result stored before offer_activity existed, so
 * replayed history gains a Start button. Rebuilt from the stored question ids
 * (the old startUrl's label used the count ASKED for, and links from before
 * `from=sage` don't pass today's allow-list). Null for the empty "drills" the
 * old tool returned with startUrl ''.
 */
export function legacyDrillAction(result: unknown): SageAction | null {
  if (!result || typeof result !== 'object') return null
  const r = result as Record<string, unknown>
  if (typeof r.startUrl !== 'string' || r.startUrl.length === 0) return null
  if (typeof r.topic !== 'string' || !Array.isArray(r.questions)) return null
  const ids = r.questions
    .map((q) => (q && typeof q === 'object' ? (q as { id?: unknown }).id : undefined))
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
  if (ids.length !== r.questions.length) return null
  const difficulty = (DIFFICULTIES as readonly unknown[]).includes(r.difficulty)
    ? (r.difficulty as SageActionDifficulty)
    : 'mixed'
  return drillAction({ ids, topic: r.topic, difficulty })
}

/**
 * The card for a stored tool result, if it has one: offer_activity's `action`,
 * or a legacy generate_drill drill. Every other tool (and a failed call) has none.
 */
export function sageActionFromToolResult(toolName: string, result: unknown): SageAction | null {
  if (toolName === 'offer_activity') {
    if (!result || typeof result !== 'object') return null
    return parseSageAction((result as { action?: unknown }).action)
  }
  if (toolName === 'generate_drill') return legacyDrillAction(result)
  return null
}

/**
 * What the model is told about a card under its reply. It wrote its lead-in
 * before the tool ran, so whenever the drill's size differs from what was
 * asked — the pool ran short, or the ask was outside what a drill holds — this
 * says the real number, and the chat route spends one more round so the reply
 * can say it (offerSettled). A drill's note also gives the card's time, so the
 * reply's estimate matches the card.
 */
export function shownNote(action: SageAction, requestedCount?: number | null): string {
  const base = `A "${action.cta}" button for "${action.label}" is shown under your reply. Refer to it; never write a link or URL.`
  if (action.kind !== 'drill' || action.count === null) return base
  const n = action.count
  const qs = `${n} ${n === 1 ? 'question' : 'questions'}`
  const time = action.note
    ? ` The card says ${qs}, ${action.note.toLowerCase()}: use that count and that time.`
    : ''
  if (typeof requestedCount !== 'number' || requestedCount === n) return `${base}${time}`
  if (requestedCount > DRILL_COUNT.max && n === DRILL_COUNT.max) {
    return `${base}${time} A drill holds at most ${DRILL_COUNT.max} questions (you asked for ${requestedCount}), so say ${n}, not ${requestedCount}.`
  }
  if (requestedCount < DRILL_COUNT.min) {
    return `${base}${time} The smallest drill is ${DRILL_COUNT.min} ${DRILL_COUNT.min === 1 ? 'question' : 'questions'}, so say ${n}.`
  }
  return `${base}${time} Only ${n} suitable ${n === 1 ? 'question was' : 'questions were'} available (you asked for ${requestedCount}), so say ${n}, not ${requestedCount}.`
}
